/**
 * Result grouping shared by the Keyword results table and the compare view's
 * rail, so a grouping chosen on the search page survives into Compare instead
 * of being re-derived (and drifting) in a second place.
 *
 * Grouping applies to the rows currently LOADED — the rail paginates, so a
 * "Load more" adds to existing groups rather than regrouping the corpus.
 */

import { regionDisplayLabel } from '../constants/filterConstants';

export type GroupByOption = 'none' | 'generic' | 'manufacturer' | 'type' | 'region';

export const REGION_LABELS: Record<string, string> = {
  US: 'US (FDA)',
  US_FDA: 'US-Drugs@FDA',
  EU: 'EU (EMA)',
  UK: 'UK (MHRA)',
  CA: 'CA (Health Canada)',
  AU: 'AU (TGA)',
  CUSTOM: 'Custom Upload',
};

/** US drugs group by application-number prefix; everything else by its
 *  regulatory body, since a non-US label carries no NDA/BLA number. */
export function deriveAppType(appNumber: string | null | undefined, region?: string | null): string {
  if (!region || region === 'US') {
    if (!appNumber) return 'Other';
    const upper = appNumber.trim().toUpperCase();
    if (upper.startsWith('NDA')) return 'NDA';
    if (upper.startsWith('BLA')) return 'BLA';
    if (upper.startsWith('ANDA')) return 'ANDA';
    return 'Other';
  }
  return REGION_LABELS[region] || region;
}

/** Region group label; renamed ("US-DailyMed") only when US-Drugs@FDA is visible. */
function regionGroupLabel(region: string | null | undefined, usFdaVisible: boolean): string {
  return regionDisplayLabel(region ?? '', usFdaVisible, REGION_LABELS[region ?? ''] || region || 'Unknown');
}

export interface GroupableRow {
  generic_name?: string | null;
  manufacturer?: string | null;
  region?: string | null;
  application_number?: string | null;
}

/** The group a row belongs to. For `generic` this is the NORMALIZED name, so
 *  "Pembrolizumab" and "PEMBROLIZUMAB" land together across regions; the
 *  human label comes back from `groupRows`. */
export function groupKeyOf(
  row: GroupableRow,
  mode: Exclude<GroupByOption, 'none'>,
  usFdaVisible = false,
): string {
  if (mode === 'generic') {
    const name = row.generic_name?.trim();
    return name ? name.toLowerCase().replace(/\s+/g, ' ') : 'Unknown Generic';
  }
  if (mode === 'manufacturer') return row.manufacturer || 'Unknown Manufacturer';
  if (mode === 'region') return regionGroupLabel(row.region, usFdaVisible);
  return deriveAppType(row.application_number, row.region);
}

export interface RowGroup<T> {
  key: string;
  /** What to show in the header: the first-seen spelling for generics. */
  label: string;
  rows: T[];
}

/** Groups rows alphabetically by label, preserving the incoming order inside
 *  each group (the rail is rank-ordered and must stay that way). Returns null
 *  when grouping is off, so callers render their flat list unchanged. */
export function groupRows<T extends GroupableRow>(
  rows: T[],
  mode: GroupByOption,
  usFdaVisible = false,
): RowGroup<T>[] | null {
  if (mode === 'none') return null;
  const byKey = new Map<string, RowGroup<T>>();
  for (const row of rows) {
    const key = groupKeyOf(row, mode, usFdaVisible);
    let group = byKey.get(key);
    if (!group) {
      const label = mode === 'generic'
        ? (row.generic_name?.trim() || 'Unknown Generic')
        : key;
      group = { key, label, rows: [] };
      byKey.set(key, group);
    }
    group.rows.push(row);
  }
  return [...byKey.values()].sort((a, b) => a.label.localeCompare(b.label));
}
